import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular"
import { DataService } from './DataServices/data.service';
import { Observable } from 'rxjs';
import { IResponse } from '../model/response.interface';
import { HttpResponse } from '@angular/common/http';
import { User } from '../model/user.model';
import { IAuthData } from '../model/authdata.interface';
import { NotificationService } from './notification.service';
import { LangsRepository } from './langs.repository';
import { IIframeResponse } from '../model/iframeresponse.interface';
import { WhatsAppDataService } from "./DataServices/whatsApp.data.service";
import { WhatsappService } from "./whatsapp.service";
import { CompanyPermission, LeadPermissionType } from "../model/leadpermission.interface";
import { environment } from '../../environments/environment';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { AppService } from './app.service';

@Injectable()
export class AuthService {
    private readonly oidcLoginRetryKey = 'oidc-login-retry';
    public authenticated = false;
    public user: User | null = null;
    public prevUrl = "";
    private readonly oidcLoginAttemptKey = 'oidc-login-attempt';
    private readonly oidcLoginAttemptTtlMs = 15000;

    constructor(
        private dataService: DataService,
        private whatsAppDataService: WhatsAppDataService,
        private notificationService: NotificationService,
        private whatsappService: WhatsappService,
        private langsRepository: LangsRepository,
        private router: Router,
        private inAppBrowser: InAppBrowser,
        private platform: Platform,
        private appService: AppService,
    ) {
        if (this.isOidcCallbackRoute()) {
            return;
        }

        let authData: IAuthData | null = this.readAuthData();
        if (authData) {
            if (authData.user) {
                this.dataService.userId.next(authData.user.id);
            }
            this.dataService.jwt.next(authData.jwt || "");
            this.dataService.token.next(authData.token);
            this.user = authData.user;
            this.notificationService.subscribe(
                this.user.id,
                !!(this.user && this.user.whatsappPhones && this.user.whatsappPhones.length > 0)
            );
            this.whatsappService.subscribe(this.user);
            // Restored session never goes through saveAuthData, so seed the error identity here too.
            this.appService.setCurrentIdentity(
                this.user ? this.user.id : null,
                this.user ? this.user.companyId : null
            );
            this.authenticated = true;
        }
    }

    get authKey(): string {
        const isInIframe = (parent !== window);
        const hostname = isInIframe ? document.referrer : window.location.hostname;
        return `fxa-${ hostname }`;
    }

    public clearOidcLoginAttempt(): void {
        try {
            sessionStorage.removeItem(this.oidcLoginAttemptKey);
        } catch (_err) {
            // Ignore sessionStorage access issues and keep login flow usable.
        }
    }

    private isOidcLoginAttemptActive(): boolean {
        try {
            const raw = sessionStorage.getItem(this.oidcLoginAttemptKey);
            if (!raw) {
                return false;
            }

            const startedAt = Number(raw);
            if (!Number.isFinite(startedAt)) {
                this.clearOidcLoginAttempt();
                return false;
            }

            if ((Date.now() - startedAt) < this.oidcLoginAttemptTtlMs) {
                return true;
            }

            this.clearOidcLoginAttempt();
            return false;
        } catch (_err) {
            return false;
        }
    }

    private markOidcLoginAttempt(): void {
        try {
            sessionStorage.setItem(this.oidcLoginAttemptKey, Date.now().toString());
        } catch (_err) {
            // Ignore sessionStorage access issues and keep login flow usable.
        }
    }

    public forgotPassword(username: string): Observable<HttpResponse<IResponse<User>>> {
        var locale: string = this.langsRepository.current.locale;
        return this.dataService.forgotPassword(username, locale);
    }

    public getToken(): string {
        return this.dataService.token.value;
    }

    public getJwt(): string {
        return this.dataService.jwt.value;
    }

    public getCurrentSessionId(): string | null {
        const persistedAuthData = this.readAuthData();
        const jwt = this.getJwt() || (persistedAuthData && persistedAuthData.jwt) || "";
        if (!jwt) {
            return null;
        }

        const payload = this.tryParseJwtPayload(jwt);
        if (!payload || typeof payload.sid !== 'string' || !payload.sid) {
            return null;
        }

        return payload.sid;
    }

    public getServerApplicationVersion(): Observable<any> {
        return this.dataService.getCurrentVersionFromServer();
    }

    public saveAuthData(authData: IAuthData): void {
        this.clearOidcLoginAttempt();
        if (authData.user) {
            this.dataService.userId.next(authData.user.id);
        }
        // Only overwrite jwt/token when explicitly provided; preserves existing auth when just updating user
        if (authData.jwt !== undefined) {
            this.dataService.jwt.next(authData.jwt || "");
        }
        if (authData.token !== undefined) {
            this.dataService.token.next(authData.token);
        }
        this.user = authData.user;
        console.log('saveAuthData', authData ? authData.user.email : "", this.authKey, authData);
        const toStore: IAuthData = {
            token: authData.token !== undefined ? authData.token : this.dataService.token.value,
            jwt: authData.jwt !== undefined ? (authData.jwt || "") : this.dataService.jwt.value,
            user: authData.user
        };
        localStorage.setItem(this.authKey, JSON.stringify(toStore));
        Sentry.configureScope(scope => {
            if (this.user) {
                scope.setUser({
                    id: this.user.id.toString(),
                    email: this.user.email,
                    username: this.user.firstName + " " + this.user.lastName,
                });
                // Lets Sentry be filtered by company when one customer reports a problem.
                if (this.user.companyId) {
                    scope.setTag("company_id", this.user.companyId.toString());
                }
            }
        });
        this.appService.setCurrentIdentity(
            this.user ? this.user.id : null,
            this.user ? this.user.companyId : null
        );

        setTimeout(() => {
            if (this.user) {
                this.notificationService.subscribe(
                    this.user.id,
                    !!(this.user && this.user.whatsappPhones && this.user.whatsappPhones.length > 0)
                );
                this.whatsappService.subscribe(this.user);
            }
        }, 1000);

        this.authenticated = true;
    }

    public logout(): void {
        this.clearOidcLoginAttempt();
        if (this.user) {
            this.notificationService.unsubscribe(this.user.id);
            this.whatsappService.unsubscribe(this.user);
        }

        console.log('logout');
        localStorage.removeItem(this.authKey);
        this.dataService.userId.next(null);
        this.dataService.jwt.next("");
        this.dataService.token.next("");
        this.user = null;
        this.appService.setCurrentIdentity(null, null);
        this.authenticated = false;
    }

    /**
     * Logout and redirect to backend. Use from menu to avoid lazy auth-module load.
     * Defers logout and redirect so menu-close animation does not interfere.
     * On Cordova, the backend logout is a multi-hop OIDC front-channel sign-out that
     * ends by redirecting back to this app's own https://localhost origin. Navigating
     * the main WebView itself there (InAppBrowser '_self') fails with
     * net::ERR_CONNECTION_REFUSED and crashes the app, since that origin only resolves
     * via the app's own internal bootstrap, not an external redirect landing on it
     * (see issue #313). Instead, run the whole chain in a separate, hidden InAppBrowser
     * window and never let it actually load that final URL — same pattern already used
     * successfully for login in startOidcLoginInAppBrowser().
     */
    public logoutAndRedirect(): void {
        const base = (environment.baseUrlV1 && environment.baseUrlV1.replace(/\/api\/crm\/webapp\/v1\/?$/, '')) || '';
        const frontReturn = `${window.location.origin}${window.location.pathname}#/`;
        const url = `${base}/api/crm/webapp/v1/auth/logout?returnUrl=${encodeURIComponent(frontReturn)}`;
        const isCordova = this.platform.is('cordova');
        const delay = isCordova ? 800 : 350;
        setTimeout(() => {
            this.logout();
            if (isCordova) {
                this.logoutInAppBrowser(url);
            } else {
                window.location.href = url;
            }
        }, delay);
    }

    private logoutInAppBrowser(url: string): void {
        const browser = this.inAppBrowser.create(url, '_blank', 'location=yes,hidden=yes');
        const appOrigin = window.location.origin;
        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            browser.close();
            this.router.navigateByUrl('/auth/login');
        };
        browser.on('loadstart').subscribe((ev: { url: string }) => {
            if ((ev.url || '').indexOf(appOrigin) === 0) finish();
        });
        browser.on('loaderror').subscribe(() => finish());
        // Safety net: never leave the hidden browser open indefinitely if neither event fires.
        setTimeout(finish, 10000);
    }

    public updateUser(user: User, token: string, jwt: string = ""): void {
        this.notificationService.unsubscribe(this.user.id);
        this.whatsappService.unsubscribe(this.user);
        this.user = user;
        let authData: IAuthData = { token: token, jwt: jwt, user: this.user };
        console.log('updateUser', authData ? authData.user.email : "", this.authKey, authData);
        localStorage.setItem(this.authKey, JSON.stringify(authData));
        setTimeout(() => {
            this.notificationService.subscribe(
                this.user.id,
                !!(this.user && this.user.whatsappPhones && this.user.whatsappPhones.length > 0)
            );
            this.whatsappService.subscribe(this.user);
        }, 1000);

        if (authData.user) {
            this.dataService.userId.next(authData.user.id);
        }
        this.dataService.jwt.next(jwt || "");
        this.dataService.token.next(token);
    }

    public updateUserSettings(user: User){
        var authData: IAuthData | null = this.readAuthData();
        if (!authData) {
            return;
        }
        console.log('updateUserSettings', authData ? authData.user.email : "", this.authKey, authData);
        authData.user = user;
        localStorage.setItem(this.authKey, JSON.stringify(authData));
    }

    public getPaymentIframe(token: string, login: string): Observable<IResponse<IIframeResponse>> {
        return this.dataService.blt_getPaymentIframe(token, this.langsRepository.current.locale, login);
    }

    public addUserBySms(token: string, smsCode: string, login: string): Observable<HttpResponse<IResponse<User>>> {
        return this.dataService.blt_addUserBySms(token, smsCode, login, this.langsRepository.current.locale);
    }

    public setWhatsAppToken(token: string) {
        this.whatsAppDataService.token.next(token);
    }

    public getUserByWhatsAppToken() {
        this.whatsAppDataService.getUserByToken().subscribe((res) => { this.user = res.data; })
    }

    public hasRestriction = (type: LeadPermissionType): boolean => {
        if (!this.user || !this.user.activeRestrictions) {
            return false;
        }
        return this.user.activeRestrictions.some((i: CompanyPermission) => i.restriction === type);
    }

    public getMe(): Observable<HttpResponse<IResponse<User>>> {
        return this.dataService.authMe();
    }

    public getOidcUrl(returnUrl?: string, companyId?: number): Observable<IResponse<{ url: string, authority: string }>> {
        return this.dataService.getOidcUrl(returnUrl, companyId);
    }

    public rememberOidcRetryRoute(route: string): void {
        if (!route) {
            return;
        }

        this.prevUrl = route;

        try {
            sessionStorage.setItem(this.oidcLoginRetryKey, route);
        } catch (_) { }
    }

    public getOidcRetryRoute(): string {
        if (this.prevUrl) {
            return this.prevUrl;
        }

        return this.getPersistedOidcRetryRoute();
    }

    public getPersistedOidcRetryRoute(): string {
        try {
            return sessionStorage.getItem(this.oidcLoginRetryKey) || '';
        } catch (_) {
            return '';
        }
    }

    public clearPersistedOidcRetryRoute(): void {
        try {
            sessionStorage.removeItem(this.oidcLoginRetryKey);
        } catch (_) { }
    }

    public consumeOidcRetryRoute(): string {
        const route = this.getOidcRetryRoute();

        this.prevUrl = '';
        this.clearPersistedOidcRetryRoute();

        return route;
    }

    /**
     * Start OIDC login by redirecting the browser to the backend initiate endpoint.
     * On Cordova, uses InAppBrowser so the main WebView stays in the app; on web, redirects in place.
     */
    public startOidcLogin(callbackUrl: string, companyId?: number): void {
        if (this.isOidcLoginAttemptActive()) {
            return;
        }

        this.markOidcLoginAttempt();
        const isCordova = this.platform.is('cordova');
        if (isCordova) {
            this.startOidcLoginInAppBrowser(callbackUrl, companyId);
            return;
        }
        this.getOidcUrl(callbackUrl, companyId).subscribe(
            res => {
                if (res && res.responseCode === 200 && res.data && res.data.url) {
                    window.location.href = res.data.url;
                    return;
                }
                this.redirectToOidcFallback(callbackUrl, companyId);
            },
            _err => {
                this.redirectToOidcFallback(callbackUrl, companyId);
            }
        );
    }

    /**
     * OIDC login in InAppBrowser so the main WebView stays on the local app.
     * When the callback URL loads, we intercept, extract token/jwt, close the browser, and navigate.
     */
    private startOidcLoginInAppBrowser(callbackUrl: string, companyId?: number): void {
        this.getOidcUrl(callbackUrl, companyId).subscribe(
            res => {
                const url = (res && res.responseCode === 200 && res.data && res.data.url)
                    ? res.data.url
                    : this.getOidcFallbackUrl(callbackUrl, companyId);
                this.openOidcInAppBrowser(url);
            },
            _err => {
                this.openOidcInAppBrowser(this.getOidcFallbackUrl(callbackUrl, companyId));
            }
        );
    }

    private openOidcInAppBrowser(url: string): void {
        const browser = this.inAppBrowser.create(url, '_blank', 'location=yes');
        const callbackPath = 'auth/callback';

        browser.on('loadstart').subscribe((ev: { url: string }) => {
            const u = ev.url || '';
            if (u.indexOf(callbackPath) === -1) return;

            const token = this.parseCallbackParam(u, 'token');
            const jwt = this.parseCallbackParam(u, 'jwt');
            const error = this.parseCallbackParam(u, 'error');

            if (error) {
                browser.close();
                this.clearOidcLoginAttempt();
                this.appService.showError('Authentication Error', `Login failed: ${error}`, true);
                return;
            }

            if (token || jwt) {
                browser.close();
                this.clearOidcLoginAttempt();
                this.dataService.jwt.next(jwt || '');
                this.dataService.token.next(token || '');
                this.clearPersistedOidcRetryRoute();
                this.getMe().subscribe(
                    meRes => {
                        if (meRes.body && meRes.body.responseCode === 200 && meRes.body.data) {
                            const user = meRes.body.data;
                            this.saveAuthData({
                                token: token || '',
                                jwt: jwt || '',
                                user
                            });
                            this.router.navigateByUrl(this.consumeOidcRetryRoute() || '/leadClients');
                        } else {
                            this.appService.showError('Authentication Error', 'Failed to get user information', true);
                            this.router.navigateByUrl('/auth/login');
                        }
                    },
                    err => {
                        this.appService.showError('Authentication Error', this.appService.getErrorMessage(err), true);
                        this.router.navigateByUrl('/auth/login');
                    }
                );
            }
        });
    }

    private parseCallbackParam(url: string, name: string): string | null {
        const hashIdx = url.indexOf('#');
        const afterHash = hashIdx >= 0 ? url.substring(hashIdx + 1) : url;
        const qIdx = afterHash.indexOf('?');
        const queryString = qIdx >= 0 ? afterHash.substring(qIdx + 1) : '';
        return new URLSearchParams(queryString).get(name);
    }

    private getOidcFallbackUrl(callbackUrl: string, companyId?: number): string {
        let base = (environment.baseUrlV1 || '').replace(/\/api\/crm\/webapp\/v1\/?$/, '');
        if (base.startsWith('http://')) base = 'https://' + base.substring(7);
        let url = `${base}/api/crm/webapp/v1/auth/initiate-oidc?returnUrl=${encodeURIComponent(callbackUrl)}`;
        if (companyId) url += `&companyId=${companyId}`;
        return url;
    }

    public getOidcCallbackUrl(): string {
        const isCordova = typeof window !== "undefined" && !!(window as any).cordova;
        if (isCordova) {
            const baseFixUrl = (environment.baseFixUrl || "").replace(/\/+$/, "");
            if (baseFixUrl) {
                return `${baseFixUrl}/#/auth/callback`;
            }
        }

        return `${window.location.origin}${window.location.pathname}#/auth/callback`;
    }

    private redirectToOidcFallback(callbackUrl: string, companyId?: number): void {
        let fallbackBaseUrl = (environment.baseUrlV1 && environment.baseUrlV1.replace(/\/api\/crm\/webapp\/v1\/?$/, ''))
            || '';
        if (fallbackBaseUrl.startsWith("http://")) {
            fallbackBaseUrl = `https://${fallbackBaseUrl.substring("http://".length)}`;
        }
        let url = `${fallbackBaseUrl}/api/crm/webapp/v1/auth/initiate-oidc?returnUrl=${encodeURIComponent(callbackUrl)}`;
        if (companyId) url += `&companyId=${companyId}`;
        window.location.href = url;
    }

    private readAuthData(): IAuthData | null {
        const raw = localStorage.getItem(this.authKey);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw);
        } catch (_err) {
            // Corrupted/stale auth payload should not break app startup.
            localStorage.removeItem(this.authKey);
            return null;
        }
    }

    private tryParseJwtPayload(jwt: string): any | null {
        try {
            const parts = jwt.split('.');
            if (parts.length < 2 || !parts[1]) {
                return null;
            }

            const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
            const decoded = atob(padded);
            return JSON.parse(decoded);
        } catch (_err) {
            return null;
        }
    }

    private isOidcCallbackRoute(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        const hash = window.location.hash || '';
        return hash.indexOf('#/auth/callback') === 0;
    }
        
}
